import Dialog from '@rio-cloud/rio-uikit/Dialog';
import Button from '@rio-cloud/rio-uikit/Button';

import { type ChangeEvent, useCallback, useEffect, useState } from 'react';
import { FormattedMessage, type IntlShape } from 'react-intl';
import {
    type Driver,
    DriverStatus,
    type DriverToCreate,
    type IdentificationTypes,
    intlDriverStatus,
    type NewIdentification,
} from '../../../../types';
import { FormInputGroup } from '../../drivers/sidebar/commonElements/FormInputGroup';
import {
    IDENTIFICATION_UNSUPPORTED_ERROR,
    IdentificationFieldRow,
    isIdentificationSupportedTenant,
} from '../../form/components/IdentificationFieldRow';
import {
    driverEmailSchema,
    driverNameSchema,
    driverPhoneNumberSchema,
    PROP_EMAIL,
    PROP_FIRST_NAME,
    PROP_IDENTIFICATION,
    PROP_LAST_NAME,
    PROP_PHONE_NUMBER,
} from '../../schema';
import type {
    CreateDriverDialogPropsFromDispatch,
    CreateDriverDialogPropsFromState,
} from '../containers/CreateDriverDialogContainer';

export type CreateDriverDialogProps = CreateDriverDialogPropsFromState & CreateDriverDialogPropsFromDispatch;

type CreateDriverDialogFormField =
    | typeof PROP_FIRST_NAME
    | typeof PROP_LAST_NAME
    | typeof PROP_EMAIL
    | typeof PROP_PHONE_NUMBER
    | typeof PROP_IDENTIFICATION;

type CreateDriverDialogFormErrors = Partial<Record<CreateDriverDialogFormField, string | undefined>>;

type CreateDriverDialogFormState = {
    firstName?: string;
    lastName?: string;
    email?: string;
    phoneNumber?: string;
    identification: string;
    identificationType?: IdentificationTypes;
    identificationValid: boolean;
};

type LastNameErrorOptions = {
    validateRequiredField?: boolean;
};

type FormErrorOptions = {
    validateRequiredFields?: boolean;
};

const createInitialDriverFormState = (): CreateDriverDialogFormState => ({
    firstName: undefined,
    lastName: undefined,
    email: undefined,
    phoneNumber: undefined,
    identification: '',
    identificationType: undefined,
    identificationValid: false,
});

const trimFieldValue = (value: string | undefined): string | undefined => value?.trim();

const getFirstNameError = (firstName: string | undefined): string | undefined => {
    return !driverNameSchema.safeParse(trimFieldValue(firstName)).success
        ? 'intl-msg:error.firstName.maxLength'
        : undefined;
};

const getLastNameError = (lastName: string | undefined, options: LastNameErrorOptions = {}): string | undefined => {
    const { validateRequiredField = lastName !== undefined } = options;
    const trimmedLastName = trimFieldValue(lastName);
    return (validateRequiredField && !trimmedLastName) || !driverNameSchema.safeParse(trimmedLastName).success
        ? 'intl-msg:error.lastName.requiredAndMaxLength'
        : undefined;
};

const getEmailError = (email: string | undefined): string | undefined => {
    return !driverEmailSchema.safeParse(trimFieldValue(email)).success ? 'intl-msg:error.email.incorrect' : undefined;
};

const getPhoneError = (phoneNumber: string | undefined): string | undefined => {
    return !driverPhoneNumberSchema.safeParse(trimFieldValue(phoneNumber)).success
        ? 'intl-msg:error.phoneNumber.invalid'
        : undefined;
};

const getIdentificationError = (
    tenant: CreateDriverDialogProps['tenant'],
    identification: string,
    identificationValid: boolean
): string | undefined => {
    if (!isIdentificationSupportedTenant(tenant)) {
        return IDENTIFICATION_UNSUPPORTED_ERROR;
    }
    return identification && !identificationValid ? IDENTIFICATION_UNSUPPORTED_ERROR : undefined;
};

const getFormErrors = (
    driverForm: CreateDriverDialogFormState,
    tenant: CreateDriverDialogProps['tenant'],
    options: FormErrorOptions = {}
): CreateDriverDialogFormErrors => {
    const { validateRequiredFields = false } = options;

    return {
        [PROP_FIRST_NAME]: getFirstNameError(driverForm.firstName),
        [PROP_LAST_NAME]: getLastNameError(driverForm.lastName, {
            validateRequiredField: validateRequiredFields || driverForm.lastName !== undefined,
        }),
        [PROP_EMAIL]: getEmailError(driverForm.email),
        [PROP_PHONE_NUMBER]: getPhoneError(driverForm.phoneNumber),
        [PROP_IDENTIFICATION]: getIdentificationError(
            tenant,
            driverForm.identification,
            driverForm.identificationValid
        ),
    };
};

function scrollToActiveDriverIfExists(retries = 3) {
    if (retries <= 0) {
        return;
    }

    setTimeout(() => {
        if (typeof document !== 'undefined') {
            const row = document?.querySelector('tr.active') ?? null;
            if (row) {
                // smooth scrolling breaks cypress in electron https://github.com/cypress-io/cypress/issues/3200
                row.scrollIntoView({ block: 'center' });
            } else {
                scrollToActiveDriverIfExists(retries - 1);
            }
        }
    }, 50);
}

export const CreateDriverDialog = (props: CreateDriverDialogProps) => {
    const [driverForm, setDriverForm] = useState<CreateDriverDialogFormState>(createInitialDriverFormState);
    const [formErrors, setFormErrors] = useState<CreateDriverDialogFormErrors>({});

    const updateState = (
        driverProp: CreateDriverDialogFormField,
        driverValue: string,
        errorMessage: string | undefined
    ) => {
        setDriverForm(prevState => ({
            ...prevState,
            [driverProp]: driverValue,
        }));
        setFormErrors(prevState => ({
            ...prevState,
            [driverProp]: errorMessage,
        }));
    };

    const handleFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateState(PROP_FIRST_NAME, value, getFirstNameError(value));
    };

    const handleLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateState(PROP_LAST_NAME, value, getLastNameError(value));
    };

    const handleEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateState(PROP_EMAIL, value, getEmailError(value));
    };

    const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateState(PROP_PHONE_NUMBER, value, getPhoneError(value));
    };

    const handleIdentificationChange = useCallback(
        (newIdentification: NewIdentification) => {
            setDriverForm(prevState => ({
                ...prevState,
                identification: newIdentification.value ? newIdentification.value : '',
                identificationValid: newIdentification.isValid,
                identificationType: newIdentification.type,
            }));
            setFormErrors(prevState => ({
                ...prevState,
                [PROP_IDENTIFICATION]: getIdentificationError(
                    props.tenant,
                    newIdentification.value ? newIdentification.value : '',
                    newIdentification.isValid
                ),
            }));
        },
        [props.tenant, setDriverForm]
    );

    useEffect(() => {
        const initialDriverForm = createInitialDriverFormState();
        setDriverForm(initialDriverForm);
        setFormErrors(getFormErrors(initialDriverForm, props.tenant));
    }, [props.showDialog, props.tenant]);

    const getCurrentFormErrors = (showMissingRequiredErrors = false): CreateDriverDialogFormErrors =>
        getFormErrors(driverForm, props.tenant, { validateRequiredFields: showMissingRequiredErrors });

    const { driverCreationInProgress } = props;
    useEffect(() => {
        if (!driverCreationInProgress) {
            scrollToActiveDriverIfExists();
        }
    }, [driverCreationInProgress]);

    const getFormattedError = (errorMessage: string | undefined) =>
        errorMessage && <FormattedMessage id={errorMessage} />;

    const isInvalidForm = (errors = formErrors): boolean => {
        const hasErrors = Object.values(errors).some(Boolean);
        const completedRequiredFields = !!(trimFieldValue(driverForm.lastName) && driverForm.identification);
        return hasErrors || !completedRequiredFields || props.driverCreationInProgress;
    };

    const onSave = () => {
        const currentFormErrors = getCurrentFormErrors(true);
        setFormErrors(currentFormErrors);
        if (isInvalidForm(currentFormErrors)) {
            return;
        }

        const driverToCreate: DriverToCreate = {
            driverId: crypto.randomUUID(),
            firstName: trimFieldValue(driverForm.firstName),
            lastName: trimFieldValue(driverForm.lastName) ?? '',
            accountId: props.accountId as string,
            status: DriverStatus.ACTIVE,
            phoneNumber: trimFieldValue(driverForm.phoneNumber),
            email: trimFieldValue(driverForm.email),
            identification: {
                id: crypto.randomUUID(),
                value: driverForm.identification,
                type: driverForm.identificationType as IdentificationTypes,
            },
        };
        props.createAndSelectDriver(driverToCreate);
    };

    const onHide = () => {
        props.toggleDriverCreationDialog(false);
    };

    const renderNameFields = () => (
        <>
            <div className='col-12 col-sm-6'>
                <FormInputGroup
                    fieldError={getFormattedError(formErrors[PROP_FIRST_NAME])}
                    fieldLabel={<FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.formLabel.firstName'} />}
                    fieldName={'firstName'}
                    value={driverForm.firstName || ''}
                    onChange={handleFirstNameChange}
                    readOnly={false}
                    type={'text'}
                    dataAttribute={'createDriverFirstNameField'}
                />
            </div>
            <div className='col-12 col-sm-6'>
                <FormInputGroup
                    fieldError={getFormattedError(formErrors[PROP_LAST_NAME])}
                    fieldLabel={
                        <span>
                            <FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.formLabel.lastName'} />*
                        </span>
                    }
                    fieldName={'lastName'}
                    value={driverForm.lastName || ''}
                    onChange={handleLastNameChange}
                    readOnly={false}
                    type={'text'}
                    dataAttribute={'createDriverLastNameField'}
                />
            </div>
        </>
    );

    const renderContactFields = () => (
        <>
            <div className='col-12 col-sm-6'>
                <FormInputGroup
                    fieldError={getFormattedError(formErrors[PROP_EMAIL])}
                    fieldLabel={<FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.formLabel.email'} />}
                    fieldName={'email'}
                    value={driverForm.email || ''}
                    onChange={handleEmailChange}
                    readOnly={false}
                    type={'email'}
                    dataAttribute={'createDriverEmailField'}
                />
            </div>
            <div className='col-12 col-sm-6'>
                <FormInputGroup
                    fieldError={getFormattedError(formErrors[PROP_PHONE_NUMBER])}
                    fieldLabel={
                        <FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.formLabel.phoneNumber'} />
                    }
                    fieldName={'phone'}
                    value={driverForm.phoneNumber || ''}
                    onChange={handlePhoneChange}
                    readOnly={false}
                    type={'text'}
                    dataAttribute={'createDriverPhoneField'}
                />
            </div>
        </>
    );

    const renderForm = () => (
        <div className='row'>
            {renderNameFields()}
            {renderContactFields()}
            <IdentificationFieldRow
                tenant={props.tenant}
                parentDialogShown={props.showDialog}
                existingDrivers={props.existingDrivers}
                onIdentificationChangeCallback={handleIdentificationChange}
                currentDriver={null}
            />
        </div>
    );

    const renderFooter = () => (
        <div data-testid={'createDriverDialogSaveButton'}>
            <Button
                autoTrackingKey='drivers.create-driver.save'
                className={props.driverCreationInProgress ? 'btn-loading-overlay' : ''}
                bsStyle={Button.PRIMARY}
                onClick={onSave}
                disabled={isInvalidForm()}
            >
                <FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.button.save'} />
            </Button>
        </div>
    );

    return (
        <Dialog
            show={props.showDialog}
            onClose={onHide}
            bsSize={Dialog.SIZE_MD}
            title={
                <span data-testid={'createDriverDialogTitle'}>
                    <FormattedMessage id={'intl-msg:createDriver.addDriver.dialog.title'} />
                </span>
            }
            footer={renderFooter()}
            body={renderForm()}
            headerButtons={
                <Button
                    aria-label='Close'
                    autoTrackingKey='drivers.create-driver.close'
                    bsStyle={Button.MUTED}
                    iconName='rioglyph-remove'
                    iconOnly
                    onClick={onHide}
                />
            }
            showCloseButton={false}
        />
    );
};

export const existingDriverErrorString = (intl: IntlShape, driver: Driver): string => {
    const intlStatus = intl.formatMessage({
        id: intlDriverStatus(driver.status),
    });
    const fullName =
        driver.firstName || driver.lastName
            ? [driver.firstName, driver.lastName].join(' ')
            : intl.formatMessage({ id: 'intl-msg:noName' });
    return `${fullName} (${intlStatus})`;
};
